/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { useNextImagePath } from '@/data/Content/_ImagePath';
import { BASE_PATH } from '@/data/constants/common';
import { concatPathComponents } from '@/utils/concatPathComponent';
import { styled } from '@mui/material/styles';
import Image from 'next/image';
import { ComponentProps, FC, useMemo, useState } from 'react';
import NextHead from 'next/head';

const StyledNextImage = styled(Image)({});
const StyledImg = styled('img')({});

const RE = /^([a-z]+:\/\/|data:image\/([a-z]+?);base64,).*$/i;
const attachBasePath = (basePath: string, src: string) =>
	RE.test(src) ? src : concatPathComponents(basePath, src);

type WithBasePath = {
	src?: string;
	srcSet?: string;
	basePath?: string;
};

// Higher-order component to handle src and srcSet logic
const withBasePath = <P extends WithBasePath>(Component: FC<P>) => {
	const WithBasePathComponent: FC<P> = (props) => {
		const { src: originalSrc, srcSet: originSrcSet, basePath, ...rest } = props;

		const src = useMemo(
			() => (originalSrc ? attachBasePath(basePath || BASE_PATH, originalSrc) : ''),
			[basePath, originalSrc]
		);

		const srcSet = useMemo(
			() =>
				originSrcSet
					? (originSrcSet as string)
							.split(',')
							.map((e) => {
								const s = e.trim().split(' ');
								s[0] = attachBasePath(BASE_PATH, s[0]);
								return s.join(' ');
							})
							.join(',')
					: '',
			[originSrcSet]
		);

		return <Component {...(rest as P)} src={src} srcSet={srcSet} />;
	};

	return WithBasePathComponent;
};

type WithPriority = {
	isPriority?: boolean;
	fetchPriority?: 'auto' | 'high' | 'low';
	loading?: 'eager' | 'lazy';
	decoding?: 'async' | 'sync' | 'auto';
	src?: string;
	id?: string;
	width?: number | string | null;
	height?: number | string | null;
};

const imageIdWithPriority = ['contentImage_1_HeaderStoreLogo_Content'];

const detectPriority = ({
	isPriority,
	width,
	height,
	id,
}: Pick<WithPriority, 'isPriority' | 'width' | 'height' | 'id'>) =>
	isPriority === undefined &&
	((Number(width) > 600 && Number(height) > 200) || (id && imageIdWithPriority.includes(id)))
		? true
		: !!isPriority;

// Higher-order component prioritize image loading and rendering
const withPriority = <P extends WithPriority>(Component: FC<P>) => {
	const WithPriorityComponent: FC<P & Pick<WithPriority, 'isPriority'>> = (props) => {
		const { isPriority, fetchPriority, loading, decoding, width, height, id, ...rest } = props;
		const [initialIsPriority] = useState<boolean>(
			detectPriority({ isPriority, height, width, id })
		);

		const [renderingProps] = useState({
			fetchpriority: fetchPriority ?? (initialIsPriority ? 'high' : 'auto'),
			loading: loading ?? (initialIsPriority ? 'eager' : 'lazy'),
			decoding: decoding ?? 'async',
			width,
			height,
			id,
		});

		if (!initialIsPriority) return <Component {...(rest as P)} {...renderingProps} />;

		return (
			<>
				<NextHead>
					<link rel="preload" href={rest.src} />
				</NextHead>
				<Component {...(rest as P)} {...renderingProps} />
			</>
		);
	};

	return WithPriorityComponent;
};

export const Img = withPriority(withBasePath(StyledImg));

// NativeImage component using the higher-order component
export const NativeImage = withPriority(
	withBasePath(({ alt, ...props }: ComponentProps<'img'>) => (
		// eslint-disable-next-line react/forbid-elements,@next/next/no-img-element
		<img alt={alt || ''} {...props} />
	))
);

export const MaterialImage = (props: ComponentProps<typeof StyledNextImage>) => {
	const { src: originalSrc, ...rest } = props;
	const src = useNextImagePath(originalSrc);
	return <StyledNextImage {...{ ...rest, src }} />;
};
